// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a ringed planet's rings on the browser globe: one MapLibre custom
 * layer per map, drawing the shared annulus of `planetRings.ts` in the
 * planet's equatorial plane around MapLibre's unit sphere, through the
 * globe's own projection matrix (`mainMatrix`, as the Satellites layer
 * draws its orbits). The ring's look is the shared 1-D profile, sampled by
 * radius in the fragment shader.
 *
 * There is no depth buffer to lean on here, so the fragment shader hides
 * every point of the ring the planet stands in front of (the segment from
 * the camera to the point passes through the unit sphere — `hiddenByBody`
 * is the same test on the CPU). The camera comes from MapLibre's horizon
 * clipping plane, exactly as the Satellites layer reads it. On the flat
 * map (2D) the layer draws nothing: there is no sphere to ring.
 *
 * `setBody` picks the body (or none); the layer stays on the map and draws
 * nothing while no ringed body shows, so a dataset change costs no
 * add/remove of layers.
 */

import type { CustomLayerInterface, CustomRenderMethodInput, Map as MapLibreMap } from 'maplibre-gl'
import { buildRingProfile, ringStrip, ringSystemOf } from './planetRings'
import { logger } from '../utils/logger'

export const PLANET_RINGS_MAP_LAYER_ID = 'ayni-planet-rings'
const SEGMENTS = 256

const VERT = `#version 300 es
precision highp float;
uniform mat4 u_matrix;
in vec3 a_pos;
in float a_t;
out vec3 v_pos;
out float v_t;
void main() {
  v_pos = a_pos;
  v_t = a_t;
  vec4 pos = u_matrix * vec4(a_pos, 1.0);
  // No depth test in this layer: z only has to stay inside the clip volume.
  pos.z = 0.0;
  gl_Position = pos;
}
`

const FRAG = `#version 300 es
precision highp float;
uniform sampler2D u_profile;
// The camera in MapLibre's unit-sphere frame, from its clipping plane.
uniform vec3 u_cam;
in vec3 v_pos;
in float v_t;
out vec4 fragColor;
void main() {
  // Hidden where the planet stands between the camera and this point.
  vec3 v = v_pos - u_cam;
  float t = clamp(-dot(u_cam, v) / dot(v, v), 0.0, 1.0);
  vec3 c = u_cam + t * v;
  if (t < 1.0 && dot(c, c) < 1.0) discard;
  vec4 col = texture(u_profile, vec2(v_t, 0.5));
  if (col.a <= 0.002) discard;
  fragColor = vec4(col.rgb * col.a, col.a);
}
`

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) ?? 'shader')
  return sh
}

export interface PlanetRingsMapLayerOptions {
  /** True while this map is the flat (2D) map: nothing is drawn. */
  isFlat: () => boolean
}

export interface PlanetRingsMapLayer extends CustomLayerInterface {
  /** The body whose rings are drawn, lower case, or null. */
  readonly body: string | null
  /** Show the rings of `body` (any case; the Earth or a ringless body = none). */
  setBody(body: string | null | undefined): void
}

export function createPlanetRingsMapLayer(opts: PlanetRingsMapLayerOptions): PlanetRingsMapLayer {
  const { isFlat } = opts
  let map: MapLibreMap | null = null
  let prog: WebGLProgram | null = null
  let vao: WebGLVertexArrayObject | null = null
  let buf: WebGLBuffer | null = null
  let idx: WebGLBuffer | null = null
  let tex: WebGLTexture | null = null
  const loc: Record<string, WebGLUniformLocation | null> = {}
  let body: string | null = null
  /** The body whose geometry and profile the GPU holds. */
  let uploaded: string | null = null
  let indexCount = 0

  function build(g: WebGL2RenderingContext): void {
    const p = g.createProgram()!
    g.attachShader(p, compile(g, g.VERTEX_SHADER, VERT))
    g.attachShader(p, compile(g, g.FRAGMENT_SHADER, FRAG))
    g.bindAttribLocation(p, 0, 'a_pos')
    g.bindAttribLocation(p, 1, 'a_t')
    g.linkProgram(p)
    if (!g.getProgramParameter(p, g.LINK_STATUS)) throw new Error(g.getProgramInfoLog(p) ?? 'link')
    prog = p
    for (const u of ['u_matrix', 'u_profile', 'u_cam']) loc[u] = g.getUniformLocation(p, u)
    buf = g.createBuffer()
    idx = g.createBuffer()
    vao = g.createVertexArray()
    g.bindVertexArray(vao)
    g.bindBuffer(g.ARRAY_BUFFER, buf)
    g.enableVertexAttribArray(0)
    g.vertexAttribPointer(0, 3, g.FLOAT, false, 16, 0)
    g.enableVertexAttribArray(1)
    g.vertexAttribPointer(1, 1, g.FLOAT, false, 16, 12)
    g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, idx)
    g.bindVertexArray(null)
    tex = g.createTexture()
    g.bindTexture(g.TEXTURE_2D, tex)
    // Mipmaps: a small globe sees the 2048-texel profile through a few
    // pixels, and without them the bands alias into moiré.
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MIN_FILTER, g.LINEAR_MIPMAP_LINEAR)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MAG_FILTER, g.LINEAR)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_S, g.CLAMP_TO_EDGE)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_T, g.CLAMP_TO_EDGE)
    uploaded = null
  }

  /** Geometry and profile of the body now chosen. */
  function upload(g: WebGL2RenderingContext, name: string): void {
    const system = ringSystemOf(name)
    if (!system) return
    g.bindVertexArray(vao)
    g.bindBuffer(g.ARRAY_BUFFER, buf)
    g.bufferData(g.ARRAY_BUFFER, ringStrip(system, SEGMENTS), g.STATIC_DRAW)
    const index = new Uint16Array(SEGMENTS * 6)
    let o = 0
    for (let s = 0; s < SEGMENTS; s++) {
      const a = s * 2
      index[o++] = a
      index[o++] = a + 1
      index[o++] = a + 2
      index[o++] = a + 1
      index[o++] = a + 3
      index[o++] = a + 2
    }
    g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, idx)
    g.bufferData(g.ELEMENT_ARRAY_BUFFER, index, g.STATIC_DRAW)
    g.bindVertexArray(null)
    indexCount = index.length
    const profile = buildRingProfile(system)
    g.bindTexture(g.TEXTURE_2D, tex)
    // MapLibre leaves its own unpack state (flipped, premultiplied) behind.
    g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, false)
    g.pixelStorei(g.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    g.pixelStorei(g.UNPACK_ALIGNMENT, 4)
    g.texImage2D(g.TEXTURE_2D, 0, g.RGBA8, profile.width, 1, 0, g.RGBA, g.UNSIGNED_BYTE, profile.rgba)
    g.generateMipmap(g.TEXTURE_2D)
    uploaded = system.body
  }

  function render(g: WebGL2RenderingContext, args: CustomRenderMethodInput): void {
    if (!prog || !body || isFlat()) return
    if (uploaded !== body) upload(g, body)
    if (!indexCount) return
    const pd = args.defaultProjectionData
    const clip = pd.clippingPlane
    // The plane faces the camera at 1/distance from the centre (MapLibre's _computeClippingPlane).
    const camDist = clip[3] < 0 ? -1 / clip[3] : 1e6
    g.useProgram(prog)
    g.uniformMatrix4fv(loc.u_matrix, false, pd.mainMatrix as Float32Array)
    g.uniform3f(loc.u_cam, clip[0] * camDist, clip[1] * camDist, clip[2] * camDist)
    g.activeTexture(g.TEXTURE0)
    g.bindTexture(g.TEXTURE_2D, tex)
    g.uniform1i(loc.u_profile, 0)
    g.disable(g.DEPTH_TEST)
    g.disable(g.CULL_FACE)
    g.enable(g.BLEND)
    g.blendFunc(g.ONE, g.ONE_MINUS_SRC_ALPHA)
    g.bindVertexArray(vao)
    g.drawElements(g.TRIANGLES, indexCount, g.UNSIGNED_SHORT, 0)
    g.bindVertexArray(null)
  }

  function free(g: WebGL2RenderingContext): void {
    if (prog) g.deleteProgram(prog)
    if (vao) g.deleteVertexArray(vao)
    if (buf) g.deleteBuffer(buf)
    if (idx) g.deleteBuffer(idx)
    if (tex) g.deleteTexture(tex)
    prog = null
    vao = null
    buf = null
    idx = null
    tex = null
    uploaded = null
    indexCount = 0
    map = null
  }

  return {
    id: PLANET_RINGS_MAP_LAYER_ID,
    type: 'custom',
    renderingMode: '2d',
    get body() {
      return body
    },
    setBody(next) {
      const name = ringSystemOf(next)?.body ?? null
      if (name === body) return
      body = name
      map?.triggerRepaint()
    },
    onAdd(m: MapLibreMap, g: WebGL2RenderingContext) {
      map = m
      try {
        build(g)
      } catch (err) {
        // MapLibre drops a failed custom layer without a word: say why.
        logger.error('[planet rings] map layer could not start:', err)
        prog = null
      }
    },
    onRemove(_m: MapLibreMap, g: WebGL2RenderingContext) {
      free(g)
    },
    render(g: WebGL2RenderingContext, args: CustomRenderMethodInput) {
      render(g, args)
    },
  }
}
